import { z } from 'zod';

import { apiUrl } from './env';

/**
 * The one place a Backoffice screen talks to `apps/api`.
 *
 * Two things it exists to get right, both of which a bare `fetch` per feature
 * would get wrong differently in each feature:
 *
 * 1. **Errors keep their `code`.** The API's exception filter deliberately
 *    echoes a wire-stable `code` alongside the status, because two of its
 *    failures share HTTP 409 and mean opposite things to an operator: a
 *    `FLAG_VERSION_CONFLICT` should re-read and show what changed, while a
 *    `FLAG_ALREADY_EXISTS` should focus the key field. Throwing a generic
 *    `Error('409')` would flatten that back out.
 * 2. **Responses are parsed, not cast.** Every response goes through a zod
 *    schema, so a drifted API surfaces here rather than as `undefined` inside a
 *    component.
 */

/** Fields the API's error responses carry. All optional — a proxy or an nginx 502 sends neither. */
const apiErrorBody = z
    .object({
        code: z.string().optional(),
        message: z.string().optional(),
        /** Present on `VALIDATION_FAILED`, one entry per bad field. */
        issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    })
    // Anything else the failure carried is kept rather than dropped. Some
    // errors are not just "what went wrong" but "what exists now": creating a
    // client is two upstream calls, and a failure in the second one answers
    // with the `tenantId` of the client that WAS created. Without it the only
    // offer the screen can make is "start again", which now fails on the
    // address the half-created client holds.
    .passthrough();

export interface ApiErrorIssue {
    readonly path: string;
    readonly message: string;
}

/**
 * A non-2xx response from the Backoffice API.
 *
 * `code` is the branch point, not `status`. Callers should switch on it and
 * treat an unrecognised code as a generic failure, so a new domain error added
 * server-side degrades to "something went wrong" rather than to a crash.
 */
export class ApiError extends Error {
    // Not a literal type: `ForbiddenError` below narrows it to its own name,
    // which a `readonly name = 'ApiError'` would make a type error.
    override readonly name: string = 'ApiError';

    constructor(
        readonly status: number,
        readonly code: string | undefined,
        message: string,
        readonly issues: readonly ApiErrorIssue[] = [],
        /**
         * Whatever else the error body carried, unparsed.
         *
         * Deliberately `unknown` values: this is a shared type and cannot know
         * every domain's extras, so each caller narrows what it asked for. See
         * {@link detail}.
         */
        readonly details: Readonly<Record<string, unknown>> = {},
    ) {
        super(message);
    }

    /** A string field of the error body, or `undefined` when absent or not a string. */
    detail(key: string): string | undefined {
        const value = this.details[key];
        return typeof value === 'string' ? value : undefined;
    }

    /** The write was based on a stale read. Re-fetch and show the operator what moved. */
    get isVersionConflict(): boolean {
        return this.code === 'FLAG_VERSION_CONFLICT';
    }

    /** The key is taken. Not retryable by re-reading — the operator must pick another. */
    get isAlreadyExists(): boolean {
        return this.code === 'FLAG_ALREADY_EXISTS';
    }

    /** Another `salesforce` flag already targets that field. Fixed by editing the field, like a taken key. */
    get isSalesforceFieldTaken(): boolean {
        return this.code === 'SALESFORCE_FIELD_TAKEN';
    }

    get isNotFound(): boolean {
        return this.status === 404;
    }
}

/**
 * The API says there is no valid Atelier session.
 *
 * Deliberately not an `ApiError`: it is not a failure of the call, it is the
 * answer, and every caller handles it the same way (bounce to the sign-in gate)
 * rather than by rendering an error inside the screen.
 */
export class UnauthenticatedError extends Error {
    override readonly name = 'UnauthenticatedError';

    constructor() {
        super('No valid Atelier session');
    }
}

/**
 * The API says you are signed in and not allowed (NOXSCRUM-1436).
 *
 * The opposite handling from {@link UnauthenticatedError}, and deliberately an
 * `ApiError` so it gets it: a 403 is the **answer**, rendered where the control
 * that failed is, and there is nothing to retry and nowhere to bounce to.
 * Sending it down the sign-in path would be the worst of both — `WriteError`
 * renders nothing for an `UnauthenticatedError`, and the session re-check would
 * find a perfectly valid session and redirect nowhere, so the operator's click
 * would silently do nothing at all.
 *
 * `code` distinguishes the two ways a route refuses: `OPERATOR_FORBIDDEN` is a
 * tier that is too low, `ROUTE_ROLE_UNDECLARED` is a server-side wiring bug on
 * a route that declared no required tier.
 */
export class ForbiddenError extends ApiError {
    override readonly name = 'ForbiddenError';

    /** A route the server has not finished wiring, not a real refusal. */
    get isRouteMisconfigured(): boolean {
        return this.code === 'ROUTE_ROLE_UNDECLARED';
    }
}

/**
 * A hung request must not become an indefinite spinner. Same reasoning as the
 * session check: nothing else is watching a static SPA's fetches, so an
 * unreachable origin would leave a query pending forever.
 */
const REQUEST_TIMEOUT_MS = 15_000;

interface RequestOptions {
    readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    readonly body?: unknown;
    readonly signal?: AbortSignal | undefined;
}

/**
 * Production's CloudFront OAC signs `/api/*` requests to the Lambda Function
 * URL origin with SigV4, but never hashes the body — AWS requires the client
 * to send the payload's hex SHA-256 as `x-amz-content-sha256`, or the origin
 * rejects every bodied request with `403 InvalidSignatureException` before it
 * reaches the app (`docs/deployment.md`). Harmless everywhere else: the dev
 * Vite proxy and the API itself ignore the header.
 */
async function payloadHash(payload: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(
        '',
    );
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
    const { method = 'GET', body, signal } = options;
    const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);

    // Serialized once so the hash provably covers the exact bytes sent — a
    // second stringify could drift and produce a valid-looking wrong hash.
    const payload = body !== undefined ? JSON.stringify(body) : undefined;

    const response = await fetch(apiUrl(path), {
        method,
        // Same-origin in dev (Vite proxy) and prod (CloudFront), so this is
        // belt-and-braces — but it is what makes a deliberately cross-origin
        // VITE_API_URL authenticate someone rather than nobody.
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...(payload !== undefined
                ? {
                      'Content-Type': 'application/json',
                      'x-amz-content-sha256': await payloadHash(payload),
                  }
                : {}),
        },
        ...(payload !== undefined ? { body: payload } : {}),
        signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    });

    // 401 and 403 are different answers and must not share a path. 401 is "we
    // do not know who you are" — bounce to Atelier. 403 is "we know exactly who
    // you are and the answer is no" — render it where the operator clicked.
    if (response.status === 401) throw new UnauthenticatedError();
    if (!response.ok) throw await toApiError(response, path);

    return response;
}

async function toApiError(response: Response, path: string): Promise<ApiError> {
    // A gateway error or a crashed handler may send HTML, or nothing at all.
    // Falling back to the status keeps a 502 from becoming a `SyntaxError` that
    // says nothing about what actually happened.
    const parsed = await response
        .json()
        .then((body: unknown) => apiErrorBody.safeParse(body))
        .catch(() => null);

    const body = parsed?.success === true ? parsed.data : undefined;
    const Failure = response.status === 403 ? ForbiddenError : ApiError;

    const { code: _code, message: _message, issues: _issues, ...details } = body ?? {};

    return new Failure(
        response.status,
        body?.code,
        body?.message ?? `${path} failed with ${String(response.status)}`,
        body?.issues ?? [],
        details,
    );
}

/**
 * Perform a request and parse its JSON body against `schema`.
 *
 * The schema's *input* is `unknown`, which is what the body actually is. Said
 * as `z.ZodType<T>` it meant `z.ZodType<T, ZodTypeDef, T>` — a schema whose
 * input already equals its output — which quietly refused every schema carrying
 * a `.catch` or a `.default`, the two ways a field absent from the wire is given
 * a value. Nothing hit it while no response schema had one. Widening costs
 * nothing: each caller still gets `T` back, and every zod schema parses unknown
 * at runtime regardless of what the type said.
 */
export async function apiRequest<T>(
    path: string,
    schema: z.ZodType<T, z.ZodTypeDef, unknown>,
    options: RequestOptions = {},
): Promise<T> {
    const response = await request(path, options);
    return schema.parse(await response.json());
}

/**
 * Perform a request that returns no body — the API's `DELETE` routes answer 204.
 * Kept separate because `response.json()` on an empty body throws, and a caller
 * passing `z.void()` to {@link apiRequest} would hit that rather than a clear
 * signature.
 */
export async function apiRequestNoContent(
    path: string,
    options: RequestOptions = {},
): Promise<void> {
    await request(path, options);
}
